import { type DiffStrategy, type DiffTreeChange, diffTrees } from '@gitbook/util-tree-merge';

import { compareReusableContents } from '../reusableContent';
import type { Revision, RevisionReusableContent, RevisionReusableContents } from '../types';

/** Reusable content from a revision, as a tree data structure */
export interface RevisionTreeReusableContent {
    reusableContent: RevisionReusableContent;
    diff?: 'added' | 'deleted' | 'modified';
}

/** Result when diffing revisions reusable contents */
export interface ReusableContentsDiffResult {
    reusableContents: RevisionTreeReusableContent[];
    diffs: Map<string, DiffTreeChange<RevisionTreeReusableContent>>;
}

/**
 * Diff two revisions and return a set of reusable contents.
 */
export function diffRevisionReusableContents(
    current: Revision,
    parent: Revision,
    options: {
        /**
         * If false, deleted reusable contents will not be included in the result.
         */
        includeDeleted?: boolean;
    } = {}
): ReusableContentsDiffResult {
    const { includeDeleted = true } = options;

    const diffStrategy: DiffStrategy<RevisionTreeReusableContent> = {
        getId: (node) => node.reusableContent.id,
        getChildren: () => [],
        setChildren: (node) => ({ ...node }),
        compareItems: (original, modified) =>
            compareReusableContents(original.reusableContent, modified.reusableContent) ? 1 : 0,
        annotateChange: (node, diff) => {
            if (!includeDeleted && diff.type === 'deleted') {
                return [];
            }
            return [
                {
                    ...node,
                    diff: diff.type,
                },
            ];
        },
    };

    const result = diffTrees(
        {
            before: createTreeReusableContents(parent.reusableContents),
            after: createTreeReusableContents(current.reusableContents),
        },
        diffStrategy
    );

    return {
        reusableContents: result.items,
        diffs: result.changes,
    };
}

/**
 * Create a tree from a set of reusable contents.
 */
export function createTreeReusableContents(
    reusableContents: RevisionReusableContents | undefined
): RevisionTreeReusableContent[] {
    if (!reusableContents) {
        return [];
    }
    return Object.values(reusableContents)
        .map((reusableContent) => ({ reusableContent }))
        .sort((a, b) => a.reusableContent.id.localeCompare(b.reusableContent.id));
}
